import axios from 'axios';

import {
  ClientWarehouse,
  RegisterInput,
  CountryThreshold,
  ClientWarehouseBE,
  AmazonConnectCredentialsModel,
  AmazonDataImportStatusModel,
  RegisterSellerInput,
  PublicClient,
  QuestionType,
  Answer,
  AmazonIntegration,
  VATId,
  FCTransfer,
  CrossedOss,
  UploadedFile,
  Payment,
  Report,
  UserCompany,
  StopMailingType,
  SellerFilingReport,
  EasybillData,
} from './types';
import { clearUserState, User } from './store/user/userSlice';
import store from './store';
import { isTokenInvalid, removeTokens } from './helpers';
import { refreshAccessToken } from './auth';
import {
  ACCESS_TOKEN_KEY,
  API_URL,
  ID_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from './constants';
import {
  normalizeClientWarehousesFE,
  normalizeCountryThresholdFE,
  normalizeFilingReport,
} from './normalization';
import { PostClientAmazonConnectInput } from '../pages/Seller/SellerAmazonConnect/SellerAmazonConnect';
import {
  setAmazonDataImportStatus,
  setHasNewAmazonCredentials,
} from './store/amazon/amazonSlice';
import { PurchasePricesUploadedFile } from '../pages/PurchasePrices/PurchasePricesUploader';

export const refreshTokens = async () => {
  try {
    const res = await refreshAccessToken(
      localStorage.getItem(REFRESH_TOKEN_KEY) || ''
    );
    localStorage.setItem(
      ACCESS_TOKEN_KEY,
      res.AuthenticationResult!.AccessToken!
    );
    localStorage.setItem(ID_TOKEN_KEY, res.AuthenticationResult!.IdToken!);
  } catch (error) {
    store.dispatch(clearUserState());
    store.dispatch(setAmazonDataImportStatus(undefined));
    store.dispatch(setHasNewAmazonCredentials(false));
    removeTokens();
  }
};

axios.interceptors.request.use(
  async function (config) {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY) || '';

    if (!accessToken) {
      return config;
    }

    if (isTokenInvalid(accessToken)) {
      await refreshTokens();
    }

    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
    };

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

export const getUser = async () => {
  const res = await axios.get<User>(`${API_URL}/get-user-data`);
  return res.data;
};

export const registerSetupCheck = async (data: RegisterInput) => {
  const res = await axios.post(`${API_URL}/register`, data);
  return res.data;
};
export const registerSeller = async (data: RegisterSellerInput) => {
  const res = await axios.post(`${API_URL}/user-register`, data);
  return res.data;
};

export async function postEvent(
  id: string,
  data: {
    type: string;
    source: string;
    block: string;
    query_string?: string;
  }
) {
  return axios.post(`${API_URL}/visitors/${id}/events`, data);
}

export const getClientThresholdByCountries = async (
  id?: string
): Promise<CountryThreshold[]> => {
  const res = await axios.get<CountryThreshold[]>(
    `${API_URL}/client/threshold/countries${id ? `?company_id=${id}` : ''}`
  );

  return (res.data || [])
    .sort((a, b) => b.threshold.value - a.threshold.value)
    .map(normalizeCountryThresholdFE);
};

export const getClientWarehouses = async (
  id?: string
): Promise<ClientWarehouse[]> => {
  const res = await axios.get<ClientWarehouseBE[]>(
    `${API_URL}/client/warehouses${id ? `?company_id=${id}` : ''}`
  );
  return (res.data || [])
    .sort((a, b) => b.count_of_sold_items - a.count_of_sold_items)
    .map(normalizeClientWarehousesFE);
};

export const getAmazonConnectCredentials = async (): Promise<
  AmazonConnectCredentialsModel[]
> => {
  const res = await axios.get(`${API_URL}/client/amazon-connect`);
  return res.data;
};

export const getAmazonDataImportStatus = async (
  controller?: AbortController
): Promise<AmazonDataImportStatusModel> => {
  const res = await axios.get(`${API_URL}/client/amazon-data-status`, {
    signal: controller?.signal,
  });
  return res.data;
};

export const postClientAmazonConnect = async ({
  company_id,
  seller_id,
  oauth_code,
}: PostClientAmazonConnectInput) => {
  const res = await axios.post(
    `${API_URL}/client/amazon-connect${
      company_id ? `?company_id=${company_id}` : ''
    }`,
    {
      seller_id,
      oauth_code,
    }
  );
  return res.data;
};

export const getClientForAmazonConnect = async (uuid: string) => {
  const res = await axios.get<PublicClient>(`${API_URL}/client-data/${uuid}`);
  return res.data;
};

export const postTriggerAmazonDataImport = async () => {
  const res = await axios.post(`${API_URL}/client/amazon-import`);
  return res.data;
};

export const patchUser = async (data: Partial<User>) => {
  const res = await axios.patch(`${API_URL}/user`, data);
  return res.data;
};

export const patchAnswer = async (
  key: QuestionType,
  answer: boolean | string
) => {
  const res = await axios.patch<Answer>(`${API_URL}/answer/${key}`, {
    answer,
  });
  return res.data;
};

export const getAnswer = async (key: QuestionType) => {
  const res = await axios.get<boolean | string>(`${API_URL}/answer/${key}`);
  return res.data;
};

export const getIntegrations = async (id?: string) => {
  const res = await axios.get<AmazonIntegration[]>(
    `${API_URL}/client/seller-accounts${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};

export const getInProgressClientVATIds = async (id?: string) => {
  const res = await axios.get<VATId[]>(
    `${API_URL}/client/vat-ids/in-progress${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};
export const getRegisteredVATIds = async (id?: string) => {
  const res = await axios.get<VATId[]>(
    `${API_URL}/client/vat-ids/registered${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};
export const getClientReports = async (id?: string) => {
  const res = await axios.get<Report[]>(
    `${API_URL}/client/reports${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};
export const getPayments90days = async (id?: string) => {
  const res = await axios.get<Payment[]>(
    `${API_URL}/client/payments${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};
export const getYearPayments = async (id?: string) => {
  const res = await axios.get<Payment[]>(
    `${API_URL}/client/payments/year${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};

export const postUserTestingOpt = async () => {
  const res = await axios.post(`${API_URL}/user/testing-opt-in`);
  return res.data;
};

export const getClientFCTransfers = async (id?: string) => {
  const res = await axios.get<FCTransfer[]>(
    `${API_URL}/client/amazon-fc-transfers${id ? `?company_id=${id}` : ''}`
  );
  return res.data;
};
export const getCrossThreshold = async (id?: string) => {
  const res = await axios.get<CrossedOss>(
    `${API_URL}/client/threshold/oss-crossed-quarter${
      id ? `?company_id=${id}` : ''
    }`
  );
  return res.data;
};
export const getManualUploadFiles = async (
  company_id: string,
  controller?: AbortController
) => {
  const res = await axios.get<UploadedFile[]>(
    `${API_URL}/client/uploaded-files${`?company_id=${company_id}`}`,
    controller
      ? {
          signal: controller?.signal,
        }
      : undefined
  );
  return res.data;
};

export const getUserCompanies = async () => {
  const res = await axios.get<UserCompany[]>(`${API_URL}/user/get-companies`);
  return res.data;
};

export const getPurchasePricesCount = async (
  company_id: string,
  controller?: AbortController
) => {
  const res = await axios.get<number>(
    `${API_URL}/client/purchase-prices/count${
      company_id ? `?company_id=${company_id}` : ''
    }`,
    controller
      ? {
          signal: controller?.signal,
        }
      : undefined
  );
  return res.data;
};

export const getPurchasePricesUploadedFiles = async (
  company_id: string,
  controller?: AbortController
) => {
  const res = await axios.get<PurchasePricesUploadedFile[]>(
    `${API_URL}/client/purchase-prices/files${
      company_id ? `?company_id=${company_id}` : ''
    }`,
    controller
      ? {
          signal: controller?.signal,
        }
      : undefined
  );
  return res.data;
};

export const postPurchasePricesTemplate = async (company_id?: string) => {
  const res = await axios.post(
    `${API_URL}/client/purchase-prices/generate-template${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};

export const postStopMailing = async (
  type: StopMailingType,
  company_id?: string
) => {
  const res = await axios.post(
    `${API_URL}/client/mailing-stop-list/${type}${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};

export const getStopMailing = async (
  type: StopMailingType,
  company_id?: string
) => {
  const res = await axios.get(
    `${API_URL}/client/mailing-stop-list/${type}${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};

export const getOngoingFiling = async (company_id?: string) => {
  const res = await axios.get<SellerFilingReport[]>(
    `${API_URL}/client/reports/ongoing${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data.map(normalizeFilingReport(false));
};

export const getCompletedFiling = async (company_id?: string) => {
  const res = await axios.get<SellerFilingReport[]>(
    `${API_URL}/client/reports/completed${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data.map(normalizeFilingReport(true));
};
export const generateConfirmationFileLink = async (
  file_id: number,
  company_id?: string
) => {
  const res = await axios.post<string>(
    `${API_URL}/client/reports/confirmation/${file_id}/download${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};
export const generatePaymentInstructionFileLink = async (
  payment_instruction_id: number,
  company_id?: string
) => {
  const res = await axios.post<string>(
    `${API_URL}/client/reports/payment-instruction/${payment_instruction_id}/download${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};

export const postConfirmPayment = async (id: number, company_id?: string) => {
  const res = await axios.post(
    `${API_URL}/client/payments/${id}/confirm${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};

export const postAgendaConfirm = async (
  report_id: number,
  company_id?: string
) => {
  const res = await axios.post(
    `${API_URL}/client/reports/${report_id}/agenda-confirm${
      company_id ? `?company_id=${company_id}` : ''
    }`
  );
  return res.data;
};

export const getEasybillTokenStatus = async (company_id?: string) => {
  const res = await axios.get<EasybillData>(`${API_URL}/client/easybill`, {
    params: {
      company_id,
    },
  });
  return res.data;
};
export const postEasybillToken = async (Token: string, company_id?: string) => {
  const res = await axios.post(
    `${API_URL}/client/easybill/save-token`,
    { Token },
    {
      params: {
        company_id,
      },
    }
  );
  return res.data;
};
